import React, { useState, useEffect, useMemo } from 'react';

// --- STYLES ---
// (Normally in a separate .css file, but included here for simplicity)
const styles = {
  container: {
    fontFamily: 'Arial, sans-serif',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    margin: '0',
    color: '#333',
  },
  error: {
    color: 'red',
    backgroundColor: '#ffebee',
    border: '1px solid red',
    padding: '10px',
    borderRadius: '4px',
  },
  loading: {
    color: '#555',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
  },
  th: {
    backgroundColor: '#f2f2f2',
    border: '1px solid #ddd',
    padding: '12px',
    textAlign: 'left',
    fontWeight: 'bold',
  },
  td: {
    border: '1px solid #ddd',
    padding: '12px',
    textAlign: 'center',
  },
  adsNameCell: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  score: {
    fontWeight: 'bold',
    fontSize: '1.1em',
    color: '#0056b3',
  },
  base: {
    fontSize: '0.8em',
    color: '#666',
    marginTop: '4px',
  },
  noDataCell: {
    color: '#999',
  }
};

/**
 * A component to fetch and display Ad Recall Top Box Scores.
 * @param {{spssFileId: number, authToken: string}} props
 */
const AdRecallTopBox = ({ spssFileId, authToken }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!spssFileId || !authToken) {
      setError("SPSS File ID and Auth Token are required.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/spss-files/${spssFileId}/ad-recall-top-box/`,
          {
            headers: {
              'Authorization': `Token ${authToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [spssFileId, authToken]); // Re-run effect if these props change

  // useMemo will prevent re-calculating the pivot table on every render
  const pivotData = useMemo(() => {
    if (!data || data.length === 0) {
      return { headers: [], rows: {} };
    }

    const uniqueAds = [...new Set(data.map(item => item.AdsName))];
    const uniqueAge = [...new Set(data.map(item => item.Age))].sort();
    const uniqueGender = [...new Set(data.map(item => item.Gender))].sort();

    const headers = uniqueAge.flatMap(age =>
      uniqueGender.map(gender => ({ age, gender }))
    );

    const rows = {};
    uniqueAds.forEach(ad => {
      rows[ad] = {};
      headers.forEach(header => {
        const key = `${header.age}-${header.gender}`;
        const item = data.find(
          d => d.AdsName === ad && d.Age === header.age && d.Gender === header.gender
        );
        rows[ad][key] = item ? { score: item.TopBoxScore, base: item.ValidN !== undefined ? item.ValidN : item.BaseSize } : null;
      });
    });

    return { headers, rows };
  }, [data]);

  if (loading) {
    return <div style={styles.container}><p style={styles.loading}>Loading Ad Recall Scores...</p></div>;
  }

  if (error) {
    return <div style={styles.container}><p style={styles.error}>Error: {error}</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Ad Recall Top Box Score Analysis</h2>
      </div>

      {data.length > 0 ? (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ad Name</th>
              {pivotData.headers.map(header => (
                <th key={`${header.age}-${header.gender}`} style={styles.th}>
                  {header.age}
                  <br />
                  ({header.gender})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(pivotData.rows).map(adsName => (
              <tr key={adsName}>
                <td style={{ ...styles.td, ...styles.adsNameCell }}>{adsName}</td>
                {pivotData.headers.map(header => {
                  const key = `${header.age}-${header.gender}`;
                  const cellData = pivotData.rows[adsName][key];
                  return (
                    <td key={key} style={styles.td}>
                      {cellData ? (
                        <div>
                          <div style={styles.score}>{cellData.score.toFixed(1)}%</div>
                          <div style={styles.base}>n={cellData.base}</div>
                        </div>
                      ) : (
                        <span style={styles.noDataCell}>-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No data available to display.</p>
      )}
    </div>
  );
};

export default AdRecallTopBox;  